// Offline edit queue for conflict detection.
//
// Why we have our own queue in addition to Firestore's offline persistence:
//   Firestore's auto-replay silently clobbers server changes that happened while
//   we were offline. To give the user a "keep yours / theirs / both" choice, we
//   need to hold back content/title edits here and reconcile them against the
//   server on reconnect.
//
// Shape: one entry per listId. Multiple offline edits to the same list merge
// into a single entry with the latest patch and the ORIGINAL baseline (the
// server state as it looked the last time we were online).

const KEY = 'lists_offline_queue_v1';

export function loadQueue() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
}

function saveQueue(queue) {
  if (Object.keys(queue).length === 0) {
    localStorage.removeItem(KEY);
  } else {
    localStorage.setItem(KEY, JSON.stringify(queue));
  }
}

// baselineUpdatedAtMs: the last known server-confirmed updatedAt (in ms), or
// null if the list was never seen online before (brand new offline list).
// baselineContent: the cloud CONTENT this edit descended from (the 3-way
// base). Walter 2026-06-22 — the timestamp baseline alone caused false
// single-device conflicts (a brief offline blip flagged a conflict against
// the device's own edit) because the timestamp can move for reasons other
// than a foreign content write. Content is the authoritative signal: if the
// server's content still equals this baseline on reconnect, nobody else
// changed it → clean commit, no conflict. Mirrors lib/historyConflict.js.
export function enqueuePatch(listId, boardId, patch, baselineUpdatedAtMs, baselineContent) {
  const queue = loadQueue();
  const existing = queue[listId];
  if (existing) {
    // Merge patches — latest values win — but keep the ORIGINAL baseline
    // (both timestamp + content), because that's what conflict detection
    // compares the server against.
    queue[listId] = {
      ...existing,
      patch: { ...existing.patch, ...patch },
      queuedAt: Date.now(),
    };
  } else {
    queue[listId] = {
      listId,
      boardId,
      patch,
      baselineUpdatedAtMs: baselineUpdatedAtMs ?? null,
      baselineContent: baselineContent ?? null,
      queuedAt: Date.now(),
    };
  }
  saveQueue(queue);
}

export function removeFromQueue(listId) {
  const queue = loadQueue();
  delete queue[listId];
  saveQueue(queue);
}

export function queueEntry(listId) {
  return loadQueue()[listId] || null;
}

export function queueSize() {
  return Object.keys(loadQueue()).length;
}
